import swal from "sweetalert";
import axiosInstance from "../../../redux/api";

/**
 * Presign: browser → API (Flask CORS). PUT: browser → S3 (S3 bucket CORS — separate).
 * API rejects file_name unless it matches: alphanumeric, hyphens, underscores, dots only (no +, parens, etc.).
 */
function sanitizeUploadFileName(name) {
  const base = (name || "image").split(/[/\\]/).pop();
  const s = base.replace(/[^a-zA-Z0-9._-]/g, "_").replace(/_+/g, "_");
  return s.length > 0 ? s : "image.png";
}

function messageFromUploadError(error) {
  const d = error?.response?.data;
  if (!d) return error?.message || "Failed to upload image";
  if (typeof d.message === "string") return d.message;
  if (typeof d.response === "string") return d.response;
  if (d.response && typeof d.response === "object") {
    try {
      return JSON.stringify(d.response);
    } catch {
      return error.message;
    }
  }
  return error?.message || "Failed to upload image";
}

export const handleImageUpload = async (
  file,
  prop,
  fieldName,
  formik,
  maxWidth,
  maxHeight,
) => {
  try {
    if (!file.type.startsWith("image/")) {
      await swal({
        title: "Invalid File Type",
        text: "Please upload an image file",
        icon: "error",
      });
      throw new Error("Please upload an image file");
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      await swal({
        title: "File Too Large",
        text: "File size should be less than 5MB",
        icon: "error",
      });
      throw new Error("File size should be less than 5MB");
    }

    if (maxWidth && maxHeight) {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(img.src);
          if (img.width > maxWidth || img.height > maxHeight) {
            reject(
              new Error(
                `Image dimensions should be ${maxWidth}x${maxHeight} or smaller`,
              ),
            );
          } else {
            resolve();
          }
        };
        img.onerror = () => reject(new Error("Failed to load image"));
      });
    }

    const fileName = `${Date.now()}-${sanitizeUploadFileName(file.name)}`;
    const folder =
      prop?.title != null && String(prop.title).trim() !== ""
        ? String(prop.title).trim()
        : "images";

    // Step 1: Get presigned upload URL from backend
    const response = await axiosInstance.post("auth/s3-url", {
      file_name: fileName,
      file_type: file.type,
      folder,
    });
    const { upload_url, file_url } = response.data.response;

    // Step 2: Upload file to S3 via presigned URL
    await fetch(upload_url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    // Step 3: Store the permanent file URL in formik
    formik.setFieldValue(fieldName, file_url);

    return { file_url, previewUrl: file_url, link: file_url };
  } catch (error) {
    console.error("Image upload failed:", error);

    await swal({
      title: "Upload Failed",
      text: messageFromUploadError(error),
      icon: "error",
    });
    throw error;
  }
};
